import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader, AlertTriangle, ArrowRight, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { fmt } from "../lib/utils";
import KpiCard from "./common/KpiCard";
import AreaChartFlow from "./common/AreaChartFlow";
import StatusBadge from "./common/StatusBadge";
import Avatar from "./common/Avatar";

export default function PortfolioView({ onSikkerhet }: { onSikkerhet: () => void }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState<any>(null);
  const [recent, setRecent] = useState<any[]>([]);
  const [overview, setOverview] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const user = JSON.parse(localStorage.getItem("contrack_user") || "null");
  const firstName = user?.full_name?.split(" ")[0] || "";

  useEffect(() => {
    Promise.all([
      api.get("/portfolio/metrics").then(r => setMetrics(r.data)).catch(() => {}),
      api.get("/portfolio/recent-invoices?limit=8").then(r => setRecent(r.data.invoices || [])).catch(() => {}),
      api.get("/portfolio/overview").then(r => setOverview(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 11) return t("greeting.morning");
    if (h < 18) return t("greeting.afternoon");
    return t("greeting.evening");
  })();

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 gap-3">
      <Loader className="w-6 h-6 animate-spin text-faint" />
      <span className="text-[13px] text-muted">{t("portfolio.laster")}</span>
    </div>
  );

  const m = metrics || {};
  const totalVal = m.total_invoice_value || {};
  const pending = m.pending_approvals || {};
  const flagged = m.flagged_at_risk || {};
  const projects = m.active_projects || {};
  const currency = m.currency || "NOK";
  const securityCount = overview?.summary?.sikkerhet_saker || 0;

  const totalDelta = totalVal.delta_pct != null ? {
    label: `${Math.abs(totalVal.delta_pct)}%`,
    direction: (totalVal.delta_pct >= 0 ? "up" : "down") as "up" | "down",
    tone: (totalVal.delta_pct >= 0 ? "pos" : "neg") as "pos" | "neg",
  } : null;

  const flaggedDelta = flagged.delta_count ? {
    label: t("portfolio.fakturaer_count", { count: Math.abs(flagged.delta_count) }),
    direction: (flagged.delta_count > 0 ? "up" : "down") as "up" | "down",
    tone: (flagged.delta_count > 0 ? "neg" : "pos") as "neg" | "pos",
  } : null;

  return (
    <div>
      {/* Greeting */}
      <div className="mb-7 animate-fade-up">
        <h1 className="text-greeting text-night">
          {greeting}{firstName ? `, ${firstName}` : ""}.{" "}
          <span className="text-faint">{t("greeting.tagline")}</span>
        </h1>
      </div>

      {/* Sikkerhetsvarsel */}
      {securityCount > 0 && (
        <div className="cx-card p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-neg/20 bg-neg/[0.03] animate-fade-up">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 bg-neg/10 rounded-full flex items-center justify-center shrink-0">
              <AlertTriangle className="w-4.5 h-4.5 text-neg" />
            </div>
            <div>
              <p className="text-[14px] font-bold text-night">
                {securityCount !== 1 ? t("portfolio.sikkerhet_alert_plural", { count: securityCount }) : t("portfolio.sikkerhet_alert", { count: securityCount })}
              </p>
              <p className="text-[12.5px] text-muted mt-0.5">{t("portfolio.ukjente_avsendere")}</p>
            </div>
          </div>
          <button onClick={onSikkerhet} className="cx-btn bg-neg text-white shrink-0">
            {t("portfolio.ga_til_sikkerhet")} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* KPI-kort */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <KpiCard
          label={t("kpi.total_invoice_value")}
          value={fmt(totalVal.value || 0, currency)}
          delta={totalDelta}
          style={{ animationDelay: "0ms" }}
        />
        <KpiCard
          label={t("kpi.pending_approvals")}
          value={pending.count ?? 0}
          sub={t("kpi.awaiting_amount", { amount: fmt(pending.awaiting_amount || 0, currency) })}
          style={{ animationDelay: "60ms" }}
        />
        <KpiCard
          label={t("kpi.flagged_at_risk")}
          value={fmt(flagged.value || 0, currency)}
          delta={flaggedDelta}
          style={{ animationDelay: "120ms" }}
        />
        <KpiCard
          label={t("kpi.active_projects")}
          value={projects.count ?? 0}
          sub={t("kpi.active_projects_sub")}
          onClick={() => navigate("/projects")}
          style={{ animationDelay: "180ms" }}
        />
      </div>

      {/* Fakturaflyt-graf (interaktiv, ekte data) */}
      <div className="mb-6 animate-fade-up" style={{ animationDelay: "120ms" }}>
        <AreaChartFlow currency={currency} />
      </div>

      {/* Siste fakturaer */}
      <div className="cx-card overflow-hidden animate-fade-up" style={{ animationDelay: "160ms" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <div>
            <h3 className="text-[15px] font-bold text-night">{t("recent.title")}</h3>
            <p className="text-[12.5px] text-muted mt-0.5">{t("recent.subtitle")}</p>
          </div>
          <button onClick={() => navigate("/invoices")} className="text-[12.5px] font-semibold text-info hover:underline inline-flex items-center gap-1">
            {t("recent.view_all")} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {recent.length === 0 ? (
          <p className="text-[13px] text-faint text-center py-12">{t("recent.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-line">
                  <th className="cx-th">{t("recent.col_customer")}</th>
                  <th className="cx-th">{t("recent.col_invoice")}</th>
                  <th className="cx-th">{t("recent.col_project")}</th>
                  <th className="cx-th text-right">{t("recent.col_amount")}</th>
                  <th className="cx-th">{t("recent.col_status")}</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((inv: any, i: number) => (
                  <tr
                    key={inv.id}
                    onClick={() => navigate(`/invoices?invoice=${inv.id}`)}
                    className={`${i > 0 ? "border-t border-line" : ""} cursor-pointer hover:bg-canvas transition-colors`}
                  >
                    <td className="cx-td">
                      <div className="flex items-center gap-2.5">
                        <Avatar name={inv.vendor_name || "?"} size="sm" />
                        <span className="font-semibold truncate max-w-[180px]">{inv.vendor_name || "—"}</span>
                      </div>
                    </td>
                    <td className="cx-td text-muted tabular">{inv.invoice_number || "—"}</td>
                    <td className="cx-td text-muted truncate max-w-[200px]">{inv.project_name}</td>
                    <td className="cx-td text-right font-bold tabular">{fmt(inv.total_amount, inv.currency)}</td>
                    <td className="cx-td"><StatusBadge status={inv.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
